import React, { useState, useEffect, Suspense, useRef, lazy } from 'react';
import { PlayIcon, CogIcon, AdjustmentsVerticalIcon, ShareIcon, ArrowLeftIcon, ChevronRightIcon, NewspaperIcon } from '@heroicons/react/20/solid'; // Import meaningful icons
import { InformationCircleIcon, TableCellsIcon, MegaphoneIcon, PaintBrushIcon, SwatchIcon } from '@heroicons/react/24/outline';  // Add this import
import Help from '../components/DropdownHelp';  // Add this import
import DefaultLoading from '../common/DefaultLoading';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/20/solid';

// Lazy load all section components
const StartScreenSection = lazy(() => import('./StartScreenSection'));
const LeaderboardSection = lazy(() => import('./LeaderboardSection'));
const CTASection = lazy(() => import('./CTASection'));
const AppearanceSection = lazy(() => import('./AppearanceSection'));
const HeadlineSetup = lazy(() => import('./EditorHeadlineSetup'));
const ShareSection = lazy(() => import('./ShareSection'));

const Form = ({ options, onOptionsUpdate, params, game, businessProfile, formRef }) => {
  const [activeSection, setActiveSection] = useState('content'); // Default to first section
  const [GameSetupComponent, setGameSetupComponent] = useState(null);

  // Load GameSetup component only once when game type changes
  useEffect(() => {
    if (game?.gameType) {
      import(`../games/${game.gameType}/GameSetup`)
        .then(module => setGameSetupComponent(() => module.default))
        .catch(error => console.error('Error loading GameSetup component', error));
    }
  }, [game?.gameType]);

  // Form sections definition
  const formSections = React.useMemo(() => [
    {
      id: 'content',
      title: 'Content',
      description: 'Configure your game content and media',
      icon: <PaintBrushIcon className="w-5 h-5" />,
      component: StartScreenSection,
      props: { options, onOptionsUpdate, params }
    },
    {
      id: 'gameSetup',  
      title: 'Game Setup',
      description: 'Configure game mechanics and settings',
      icon: <CogIcon className="w-5 h-5" />,
      component: GameSetupComponent,
      props: { options, onOptionsUpdate }
    },
    {
      id: 'appearance',
      title: 'Appearance',
      description: 'Customize colors, fonts, and visual styles',
      icon: <SwatchIcon className="w-5 h-5" />,
      component: AppearanceSection,
      props: { options, onOptionsUpdate, game }
    },
    {
      id: 'header',
      title: 'Header',
      description: 'Configure header visibility and appearance',
      icon: <NewspaperIcon className="w-5 h-5" />,
      component: HeadlineSetup,
      props: { options, onOptionsUpdate },
      badge: !options?.hideHeadline ? 'enabled' : 'disabled'
    },
    {
      id: 'leaderboard',
      title: 'Leaderboard',
      description: 'Configure leaderboard settings',
      icon: <TableCellsIcon className="w-5 h-5" />,
      component: LeaderboardSection,
      props: { options, onOptionsUpdate },
      badge: options?.leaderboard?.enabled ? 'enabled' : 'disabled'
    },
    {
      id: 'callToAction',
      title: 'Call to Action',
      description: 'Configure primary action button',
      icon: <MegaphoneIcon className="w-5 h-5" />,
      component: CTASection,
      props: { options, onOptionsUpdate },
      badge: options?.ctaEnabled ? 'enabled' : 'disabled'
    },
    {
      id: 'sharing',
      title: 'Sharing',
      description: 'Configure social sharing options',
      icon: <ShareIcon className="w-5 h-5" />,
      component: ShareSection,
      props: { options, onOptionsUpdate, params }
    }
  ], [options, onOptionsUpdate, params, GameSetupComponent, game]);

  // Render the active section's content
  const renderSectionContent = (section) => {
    if (!section.component) return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <div className="text-center">
          <CogIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>This section is not available for this game type.</p>
        </div>
      </div>
    );
    
    const Component = section.component;
    return (
      <Suspense fallback={
        <div className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center space-y-3">
            <div 
              className="w-8 h-8 border-3 rounded-full animate-spin"
              style={{
                borderColor: 'rgba(241, 196, 15, 0.3)',
                borderTopColor: '#F1C40F'
              }}
            />
            <p className="text-sm font-bold tracking-wide" style={{
              color: '#F1C40F',
              fontFamily: '"Share Tech Mono", monospace'
            }}>
              Loading...
            </p>
          </div>
        </div>
      }>
        <Component {...section.props} />
      </Suspense>
    );
  };

  const activeTab = formSections.find(s => s.id === activeSection);

  return (
    <div className="h-full flex flex-col md:flex-row" style={{
      fontFamily: '"Share Tech Mono", monospace'
    }}>
      {/* Mobile Navigation - Show first on mobile */}
      <div className="md:hidden flex-shrink-0 border-b-2" style={{
        borderColor: 'rgba(241, 196, 15, 0.3)',
        background: 'rgba(52, 73, 94, 0.8)'
      }}>
        {/* Mobile Section Header */}
        <div className="px-4 py-3 border-b" style={{
          borderColor: 'rgba(241, 196, 15, 0.2)',
          background: 'rgba(44, 62, 80, 0.6)'
        }}>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full animate-pulse" style={{backgroundColor: '#F1C40F'}}></div>
            <span className="text-xs font-bold tracking-wide" style={{color: '#F1C40F'}}>
              {activeTab?.title || 'CONTENT'}
            </span>
          </div>
        </div>

        {/* Mobile Section Navigation - Horizontal Scroll */}
        <div className="overflow-x-auto">
          <div className="flex gap-2 p-3 min-w-max">
            {formSections.map((section, index) => (
              <button
                key={section.id}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveSection(section.id);
                }}
                className="flex-shrink-0 rounded-lg transition-all duration-200 min-w-0"
                style={{
                  background: activeSection === section.id 
                    ? 'linear-gradient(135deg, #E74C3C, #C0392B)' 
                    : 'rgba(52, 73, 94, 0.6)',
                  color: activeSection === section.id ? '#FFFFFF' : '#F1C40F',
                  border: '1px solid rgba(241, 196, 15, 0.4)',
                  padding: '8px 12px'
                }}
              >
                <div className="flex items-center gap-2">
                  {/* Icon */}
                  <div className="flex-shrink-0" style={{
                    color: activeSection === section.id ? '#FFFFFF' : '#F1C40F'
                  }}>
                    {React.cloneElement(section.icon, { className: 'w-4 h-4' })}
                  </div>

                  {/* Title */}
                  <span className="font-bold text-xs tracking-wide whitespace-nowrap">
                    {section.title}
                  </span>
                  
                  {/* Badge for mobile */}
                  {section.badge && (
                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{
                      backgroundColor: section.badge === 'enabled' ? '#2ECC71' : '#95A5A6'
                    }}></div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Desktop Navigation - Hidden on mobile */}
      <div className="hidden md:flex flex-shrink-0 w-80 flex-col" style={{
        background: 'rgba(52, 73, 94, 0.6)',
        borderRight: '2px solid rgba(241, 196, 15, 0.4)'
      }}>
        <div className="p-4 border-b-2" style={{
          borderColor: 'rgba(241, 196, 15, 0.3)',
          background: 'rgba(44, 62, 80, 0.4)'
        }}>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-3 h-3 rounded-full animate-pulse" style={{backgroundColor: '#F1C40F'}}></div>
            <h3 className="text-xs font-bold tracking-wide" style={{
              color: '#F1C40F'
            }}>
              GAME CONFIG
            </h3>
          </div>
          <p className="text-xs font-bold tracking-wide" style={{color: '#95A5A6'}}>
            Configure your game settings
          </p>
        </div>
        
        <nav className="flex-1 p-3 overflow-y-auto">
          <div className="space-y-2">
            {formSections.map((section, index) => (
              <button
                key={section.id}
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveSection(section.id);
                }}
                className="w-full group relative overflow-hidden rounded-lg transition-all duration-200"
                style={{
                  background: activeSection === section.id 
                    ? 'linear-gradient(135deg, #E74C3C, #C0392B)' 
                    : 'rgba(52, 73, 94, 0.6)',
                  color: activeSection === section.id ? '#FFFFFF' : '#F1C40F',
                  border: '1px solid rgba(241, 196, 15, 0.4)'
                }}
                onMouseEnter={(e) => {
                  if (activeSection !== section.id) {
                    e.target.style.background = 'rgba(241, 196, 15, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeSection !== section.id) {
                    e.target.style.background = 'rgba(52, 73, 94, 0.6)';
                  }
                }}
              >
                <div className="flex items-center gap-3 p-3">
                  {/* Icon */}
                  <div className="flex-shrink-0 p-2 rounded-lg" style={{
                    background: activeSection === section.id 
                      ? 'rgba(255, 255, 255, 0.2)' 
                      : 'rgba(241, 196, 15, 0.1)',
                    color: activeSection === section.id ? '#FFFFFF' : '#F1C40F'
                  }}>
                    {section.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0 text-left">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs truncate tracking-wide">
                        {section.title}
                      </span>
                      
                      {/* Badge - moved to same line */}
                      {section.badge && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold tracking-wide" style={{
                          background: section.badge === 'enabled' 
                            ? 'rgba(46, 204, 113, 0.2)'
                            : 'rgba(149, 165, 166, 0.2)',
                          color: section.badge === 'enabled' ? '#2ECC71' : '#95A5A6'
                        }}>
                          <div className="w-1.5 h-1.5 rounded-full mr-1.5" style={{
                            backgroundColor: section.badge === 'enabled' ? '#2ECC71' : '#95A5A6'
                          }}></div>
                          {section.badge === 'enabled' ? 'ENABLED' : 'DISABLED'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Content Area - Works for both desktop and mobile */}
      <div className="flex-1 overflow-y-auto min-h-0" style={{
        background: 'linear-gradient(135deg, #2C3E50, #34495E)'
      }}>
        {activeTab && (
          <div className="p-4 md:p-8">
            {/* Section Header - Hidden on mobile since it's in the mobile nav */}
            <div className="hidden md:block mb-8 pb-6 border-b-2" style={{
              borderColor: 'rgba(241, 196, 15, 0.3)'
            }}>
              <div className="mb-4">
                <h2 className="text-2xl font-bold tracking-wide mb-2" style={{
                  color: '#F1C40F',
                  fontFamily: '"Share Tech Mono", monospace'
                }}>
                  {activeTab.title}
                </h2>
                <p className="text-sm font-bold tracking-wide" style={{color: '#95A5A6'}}>
                  {activeTab.description}
                </p>
              </div>
            </div>

            {/* Mobile Section Description */}
            <div className="md:hidden mb-4 p-3 rounded-lg" style={{
              background: 'rgba(241, 196, 15, 0.1)',
              border: '1px solid rgba(241, 196, 15, 0.3)'
            }}>
              <p className="text-xs font-bold tracking-wide" style={{color: '#95A5A6'}}>
                {activeTab.description}
              </p>
            </div>

            {/* Section Content - Wrapped in form for validation */}
            <div className="rounded-xl p-3 md:p-6" style={{
              background: 'rgba(52, 73, 94, 0.4)',
              border: '2px solid rgba(241, 196, 15, 0.2)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
            }}>
              <form ref={formRef}>
                <div className="space-y-6 md:space-y-8">
                  {renderSectionContent(activeTab)}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Form;